import {
  OrderSide,
  Symbol,
  Level1SnapshotJSON,
  Direction,
  OrderRecord,
  OrderAlgoParameters,
  TradingAccount,
  CancelableStatuses,
  OrderState,
  AlgoParams,
  ManualOrderParams,
  OrderEntryOrder,
  OrderType,
  AlgoParameterDefinition,
  OrderRequest,
  JpmAlgoTagStrings,
} from '@qep/types/src/index';
import {OrderTypes} from './algos';
import {getLimitPriceTickError, getStopPriceError} from './price-tick-validation';

export interface OrderRecordWithPrices extends OrderRecord {
  symbolDetails: Symbol;
  prices: Level1SnapshotJSON;
}

export interface Order {
  tradingAccount: TradingAccount;
  ticketType: OrderTypes;
  legs: OrderRecord[];
  orderId: string;
  params: OrderAlgoParameters;
  broker: string;
}

export interface OrderTCA {
  manual: string;
  algo: string;
}

export interface MarketImpact {
  marketImpact: string;
}

export interface S2F {
  sweepToFill: string;
}

type AddInfoType = {isIceberg?: boolean} | AlgoParameterDefinition[];

/** Returns true when the manual order type has a Limit Price field that must obey product tick validation. */
function manualOrderTypeRequiresLimitPrice(orderTypeNum: number | undefined): boolean {
  return orderTypeNum === OrderType.ORDERTYPE_LIMIT || orderTypeNum === OrderType.ORDERTYPE_STOP_LIMIT;
}

/** Returns true when the manual order type has a Stop Price field that must pass shared validation. */
function manualOrderTypeRequiresStopPrice(orderTypeNum: number | undefined): boolean {
  return orderTypeNum === OrderType.ORDERTYPE_STOP || orderTypeNum === OrderType.ORDERTYPE_STOP_LIMIT;
}

/** Builds the manual Limit Price tick error from the first order leg's instrument metadata. */
export function getManualLimitPriceTickError(order: OrderEntryOrder): string | undefined {
  const params = order.params as ManualOrderParams;
  const orderTypeNum = params.orderType ? Number(params.orderType) : undefined;
  if (!manualOrderTypeRequiresLimitPrice(orderTypeNum)) {
    return undefined;
  }

  return getLimitPriceTickError(params.limitPrice, order.legs[0]?.symbol?.tickSize);
}

/** Builds the complete manual Stop Price error from the first order leg's instrument metadata. */
export function getManualStopPriceError(order: OrderEntryOrder): string | undefined {
  const params = order.params as ManualOrderParams;
  const orderTypeNum = params.orderType ? Number(params.orderType) : undefined;
  if (!manualOrderTypeRequiresStopPrice(orderTypeNum)) {
    return undefined;
  }

  return getStopPriceError(params.stopLimit, order.legs[0]?.symbol?.tickSize);
}

/** Validates all manual order fields required before the order can be submitted to the OMS. */
function isManualOrderValid(order: OrderEntryOrder, options?: AddInfoType) {
  if (order.legs.length === 1 && Number(order.legs[0].quantity) > 0) {
    const params: ManualOrderParams = (order.params as ManualOrderParams);
    const orderTypeNum = params.orderType ? Number(params.orderType) : undefined;

    if (orderTypeNum === OrderType.ORDERTYPE_LIMIT) {
      if (!params.limitPrice) {
        return false;
      }

      if ((options as {isIceberg: boolean})?.isIceberg) {
        const numberDisplayQuantity = Number(params.displayQuantity);
        if (
          numberDisplayQuantity === 0
          || Number.isNaN(numberDisplayQuantity)
          || numberDisplayQuantity > Number(order.legs[0].quantity)
        ) {
          return false;
        }
      }
    }

    if (orderTypeNum === OrderType.ORDERTYPE_STOP_LIMIT && !params.limitPrice) {
      return false;
    }

    if (getManualLimitPriceTickError(order)) {
      return false;
    }

    if (getManualStopPriceError(order)) {
      return false;
    }

    if (!order.legs[0].account) {
      return false;
    }

    return true;
  }

  return false;
}

function timeStringIsValid(time?: string): boolean {
  if (!time) {
    return false;
  }

  return new RegExp(/\d{1,2}:\d{1,2}/gi).test(time);
}

function isAlgoOrderValid(order: OrderEntryOrder, algoDefs: AlgoParameterDefinition[]): boolean {
  const params = order.params as AlgoParams;
  if (order.legs.length > 0) {
    // Check all legs have quantity and account
    for (let i = 0; i < order.legs.length; i++) {
      const leg = order.legs[i];
      if (Number(leg.quantity) === 0) {
        return false;
      }

      if (!leg.account) {
        return false;
      }
    }

    // Check that start time is not a bad value and launch immediately is false
    // @ts-ignore
    if (!params.launchImmediately && params.startTime === '-1') {
      return false;
    }

    if (!timeStringIsValid(params.duration) && !timeStringIsValid(params.endTime)) {
      return false;
    }

    if (params.duration?.toString().trim() === ':' && params.endTime?.toString().trim() === ':') {
      return false;
    }

    if (!params.endTime && !params.duration) {
      return false;
    }

    if (params.endTime) {
      const endDate = new Date(new Date().setHours(params.endTime.split(':')[0], params.endTime.split(':')[1], 0, 0));
      if (params.launchImmediately && endDate <= new Date()) {
        return false;
      }

      // @ts-ignore
      const startDate = new Date(new Date().setHours(params.startTime?.split(':')[0], params.startTime?.split(':')[1], 0, 0));
      if (params.startTime && endDate <= startDate) {
        return false;
      }
    }

    // Check that number of legs matches the requires number of legs for given algo type
    for (let k = 0; k < algoDefs.length; k++) {
      const algoParam = algoDefs[k];
      if (!params[algoParam.algoParameterId] && !algoParam.hasOwnProperty('isOptional') && !algoParam?.hasDefault) {
        return false;
      }

      if (algoParam.algoParameterId.includes('POSSIBLE_LEGS') && (order.legs.length < Number(algoParam.numericMinValue) || order.legs.length > Number(algoParam.numericMaxValue))) {
        return false;
      }
    }

    return true;
  }

  return false;
}

function isJpmAlgoOrder(order: OrderRecord): boolean {
  return order.algoType !== undefined && Object.keys(JpmAlgoTagStrings).includes(order.algoType);
}

export function isOrderValid(
  order: OrderEntryOrder,
  addInfo?: AlgoParameterDefinition[] | {isIceberg?: boolean},
): boolean {
  if (order.ticketType === OrderTypes.Manual) {
    return isManualOrderValid(order, Array.isArray(addInfo) ? undefined : addInfo);
  }

  if (order.ticketType === OrderTypes.Algo || order.ticketType === OrderTypes.Spread) {
    return isAlgoOrderValid(order, Array.isArray(addInfo) ? addInfo : []);
  }

  return false;
}

/** Routes a completed order-entry payload to the manual-order placement boundary. */
export async function placeOrder(order: OrderEntryOrder) {
  return placeManualOrder(order);
}

/** Places one manual order after rejecting incomplete payloads and inactive price fields before the OMS boundary. */
const placeManualOrder = async (order: OrderEntryOrder): Promise<OrderRecord> => {
  try {
    const leg = order.legs[0];
    const account = leg?.account as TradingAccount | undefined;
    if (!leg || !account) {
      throw new Error('An account is required to place an order.');
    }

    const parameters = order.params as ManualOrderParams;
    const orderType = Number(parameters.orderType);
    const hasStopTrigger = orderType === Number(OrderType.ORDERTYPE_STOP)
      || orderType === Number(OrderType.ORDERTYPE_STOP_LIMIT);

    const limitPriceTickError = getManualLimitPriceTickError(order);
    if (limitPriceTickError) {
      throw new Error(limitPriceTickError);
    }

    const stopPriceError = getManualStopPriceError(order);
    if (stopPriceError) {
      throw new Error(stopPriceError);
    }

    const placedOrder = await globalThis.QEP.apiServices.ordermanager.placeOrder({
      symbol: leg.symbol,
      side:
        leg.direction === Direction.Sell
          ? OrderSide.ORDERSIDE_SELL
          : OrderSide.ORDERSIDE_BUY,
      quantity: leg.quantity,
      displayQuantity: parameters.displayQuantity, // May be undefined
      type: orderType,
      limit_price: parameters.limitPrice, // May be undefined
      ...(hasStopTrigger ? {stop_limit: parameters.stopLimit} : {}),
      time_in_force: Number(parameters.timeInForce),
      expirationDate: parameters.expirationDate,
      accountId: account.accountId,
      execCode: account.execCode,
      memo: account.memo, // May be undefined
      clearingString: account.clearingString,
      customTag1: account.notes[0], // May be undefined
      customTag2: account.notes[1], // May be undefined
      customTag3: account.notes[2], // May be undefined
    } as OrderRequest);
    return placedOrder;
  } catch (error) {
    throw error;
  }
};

export function canModifyOrder(order?: OrderRecord | null): boolean {
  if (!order) {
    return false;
  }

  if (order?.algoRunId) {
    return false;
  }

  if (isJpmAlgoOrder(order)) {
    return false;
  }

  if (Number(order.orderType) === OrderType.ORDERTYPE_STOP_LIMIT) {
    return false;
  }

  const status = isNaN(Number(order.status)) ? order.status : Number(order.status);
  if (status === undefined || status === null) {
    return false;
  }

  return CancelableStatuses.includes(status as OrderState)
    && (order.hasOwnProperty('orderSource') && (order.orderSource?.toLowerCase().includes('qep') ?? false));
}
